// src/services/searchClient.js
import algoliasearch from 'algoliasearch/lite';
import {
  getFunctions,
  httpsCallable /* , connectFunctionsEmulator */,
} from 'firebase/functions';
import { app, db, auth } from './firebase';
import {
  collection,
  getDocs,
  orderBy,
  limit,
  query as fsQuery,
  where,
} from 'firebase/firestore';

/* =========================
   ENV (Vite) / Config
   ========================= */
// Use prefixo VITE_ no build do Vite. Mantemos fallback para compat local.
const ENV_APP_ID =
  import.meta.env.VITE_ALGOLIA_APP_ID || import.meta.env.ALGOLIA_APP_ID;
const ENV_SEARCH = import.meta.env.VITE_ALGOLIA_SEARCH_KEY; // Search-Only key
const ENV_INDEX =
  import.meta.env.VITE_ALGOLIA_INDEX ||
  import.meta.env.ALGOLIA_INDEX ||
  'imoveis';

const FUNCTIONS_REGION = 'us-central1';

// Functions client na mesma região das callables (Gen2)
const functions = getFunctions(app, FUNCTIONS_REGION);
// if (import.meta.env.VITE_USE_FN_EMULATOR === '1') connectFunctionsEmulator(functions, 'localhost', 5001);

// memo POR HANDLE — armazena o *resultado* ou a *Promise* para dedupe
const memo = new Map(); // key => Promise<{ type, client, index, indexName, appId } | { type: 'firestore' }>

/* =========================
   Helpers de URL e Slug
   ========================= */
function getHandleFromLocation() {
  try {
    const { hostname, pathname } = window?.location || {};
    // 1) subdomínio: handle.dominio.com
    if (hostname) {
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        const sub = (parts[0] || '').toLowerCase();
        if (sub && !['www', 'app', 'dev', 'stage', 'localhost'].includes(sub)) {
          return sub;
        }
      }
    }
    // 2) rota /u/:handle
    const m = pathname?.match(/\/u\/([a-z0-9-]+)/i);
    if (m) return m[1].toLowerCase();
    return null;
  } catch {
    return null;
  }
}

function memoKey(handle) {
  return handle || '__ENV__';
}

const toSlug = (s = '') =>
  String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/* =========================
   Algolia Clients
   ========================= */
function fromEnv() {
  if (!ENV_APP_ID || !ENV_SEARCH) return null;
  const idx = ENV_INDEX;
  const client = algoliasearch(ENV_APP_ID, ENV_SEARCH);
  const index = client.initIndex(idx);
  return { type: 'algolia', client, index, indexName: idx, appId: ENV_APP_ID };
}

async function callKey(fnName, handle) {
  const fn = httpsCallable(functions, fnName);
  try {
    const { data } = await fn({ handle });
    return data;
  } catch (err) {
    err.__callable = fnName;
    throw err;
  }
}

/** Pede secured key no backend (restrita por dono+sócios). */
async function fromCallable(handle) {
  if (!handle) return null;

  let data = null;
  try {
    data = await callKey('getSiteSearchKey', handle);
    if (!data?.appId || !data?.apiKey) {
      data = await callKey('getAlgoliaSearchKey', handle);
    }
  } catch {
    try {
      data = await callKey('getAlgoliaSearchKey', handle);
    } catch (err2) {
      console.warn('[Algolia] callables falharam:', err2?.message || err2);
      return null;
    }
  }

  if (data?.appId && data?.apiKey) {
    // ⚠️ use SEMPRE o índice que o backend retornou (ou ENV como fallback)
    const idx = data.index || ENV_INDEX;
    const client = algoliasearch(data.appId, data.apiKey);
    const index = client.initIndex(idx);
    if (data?.debug?.filters) {
      console.info('[Algolia] secured filters:', data.debug);
    }
    return {
      type: 'algolia',
      client,
      index,
      indexName: idx,
      appId: data.appId,
    };
  }

  console.warn('[Algolia] callable(s) retornaram vazio.');
  return null;
}

/**
 * Engine de busca:
 * - Microsite (tem handle): Callable → ENV → Firestore
 * - App interno (sem handle): ENV → Callable → Firestore
 */
export async function getSearchClient({ handle = null } = {}) {
  const resolvedHandle = handle ?? getHandleFromLocation();
  const key = memoKey(resolvedHandle);

  if (memo.has(key)) return memo.get(key);

  const promise = (async () => {
    let engine = null;
    if (resolvedHandle) {
      engine = await fromCallable(resolvedHandle);
      if (!engine) engine = fromEnv();
    } else {
      engine = fromEnv();
      if (!engine) engine = await fromCallable(resolvedHandle);
    }
    if (!engine) engine = { type: 'firestore' };

    if (engine.type === 'algolia') {
      console.info('[Algolia] engine pronto:', {
        appId: engine.appId,
        index: engine.indexName,
        via: resolvedHandle ? 'callable' : 'env',
      });
    } else {
      console.info('[Algolia] usando fallback Firestore');
    }
    return engine;
  })();

  memo.set(key, promise);
  return promise;
}

/* =========================
   Resolvedor de UID por handle
   ========================= */
async function resolveUidByHandle(handle) {
  const h = String(handle || '')
    .toLowerCase()
    .trim();
  if (!h) return null;
  const snap = await getDocs(
    fsQuery(
      collection(db, 'users'),
      where('siteHandle', '==', h),
      where('siteEnabled', '==', true),
      limit(1)
    )
  );
  return snap.empty ? null : snap.docs[0].id;
}

/* =========================
   Busca principal (Algolia / Firestore fallback)
   ========================= */
export async function searchProperties({
  queryText = '',
  page = 0,
  hitsPerPage = 24,
  params = {},
  handle = null,
} = {}) {
  const resolvedHandle = handle ?? getHandleFromLocation();

  // Deixe o backend decidir o índice (base/réplica)
  const engine = await getSearchClient({ handle: resolvedHandle });

  if (engine.type === 'algolia') {
    try {
      // Nunca envie `indexName` nos search params
      const { indexName: _ignored, ...searchParams } = params || {};

      const res = await engine.index.search(queryText, {
        page,
        hitsPerPage,
        ...searchParams,
      });

      return {
        items: res.hits || [],
        total: res.nbHits ?? 0,
        engine: 'algolia',
        indexName: engine.indexName,
      };
    } catch (e) {
      console.warn(
        '[Algolia] erro na busca; usando Firestore:',
        e?.message || e
      );
      // cai no fallback
    }
  }

  // --------- Firestore fallback ---------
  if (resolvedHandle) {
    try {
      const uid = await resolveUidByHandle(resolvedHandle);
      if (uid) {
        const catSnap = await getDocs(
          fsQuery(
            collection(db, 'siteCatalog', uid, 'imoveis'),
            orderBy('createdAt', 'desc'), // siteCatalog mantém createdAt
            limit(hitsPerPage)
          )
        );
        const items = catSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        return { items, total: items.length, engine: 'firestore:siteCatalog' };
      }
    } catch (e) {
      console.warn(
        '[FS fallback] siteCatalog falhou, usando imoveis:',
        e?.message || e
      );
    }
  }

  // ✅ Fallback final: coleção "imoveis" com os campos atuais
  const q = fsQuery(
    collection(db, 'imoveis'),
    where('status', '==', 'publicado'),
    orderBy('criadoEm', 'desc'),
    limit(hitsPerPage)
  );
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return { items, total: items.length, engine: 'firestore' };
}

/* =========================
   Builder de parâmetros (Microsite)
   ========================= */
export function buildMicrositeAlgoliaParamsFromSearch(search) {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;

  const facetFilters = [];
  const numericFilters = [];

  // ── parâmetros de URL (com fallbacks) ─────────────────────────────
  const ref = sp.get('ref') || sp.get('codigo') || '';
  const estado = sp.get('estado') || sp.get('uf') || '';
  const cidade = sp.get('cidade') || '';
  const bairro = sp.get('bairro') || '';
  const local = sp.get('local') || ''; // texto livre (query)

  // referência
  if (ref) facetFilters.push(`refCodigo:${ref}`);

  // UF
  if (estado) facetFilters.push([`estado_slug:${toSlug(estado)}`]);

  // Cidade: simples OR full (compat)
  if (cidade) {
    const cidadeOR = [`cidade_slug:${toSlug(cidade)}`];
    if (estado)
      cidadeOR.push(`cidade_slug_full:${toSlug(`${estado}-${cidade}`)}`);
    facetFilters.push(cidadeOR);
  }

  // Bairro: simples OR full (compat)
  if (bairro) {
    const bairroOR = [`bairro_slug:${toSlug(bairro)}`];
    if (estado && cidade) {
      bairroOR.push(
        `bairro_slug_full:${toSlug(`${estado}-${cidade}-${bairro}`)}`
      );
    }
    facetFilters.push(bairroOR);
  }

  // tipo (slug)
  const tipo = sp.get('tipo');
  if (tipo) facetFilters.push(`tipo_slug:${toSlug(tipo)}`);

  // números
  const n = (v) => (v ? Number(String(v).replace(/[^\d]/g, '')) : NaN);
  const min = n(sp.get('min'));
  const max = n(sp.get('max'));
  const dorm = sp.get('dorm'); // pode vir "2","3","4+"

  if (!Number.isNaN(min)) numericFilters.push(`preco>=${min}`);
  if (!Number.isNaN(max)) numericFilters.push(`preco<=${max}`);

  if (dorm) {
    if (dorm === '2' || dorm === '3') {
      numericFilters.push(`dormitorios=${Number(dorm)}`); // 2/3 exato
    } else {
      const d = Number(String(dorm).replace(/\D/g, '')) || 4; // 4+ mínimo
      numericFilters.push(`dormitorios>=${d}`);
    }
  }

  // termo livre
  const queryText = local;

  // ⚠️ Não force indexName/filters no front — a secured key já restringe.
  return {
    queryText,
    params: {
      facetFilters: facetFilters.length ? facetFilters : undefined,
      numericFilters: numericFilters.length ? numericFilters : undefined,
    },
  };
}

/* =========================
   Grupos do usuário atual
   ========================= */
export async function getGroups() {
  const uid = auth?.currentUser?.uid || null;
  const accepted = ['accepted', 'aceito', 'ativo', 'active', 'approved'];

  let socios = [];
  try {
    if (uid) {
      const q1 = fsQuery(
        collection(db, 'socios'),
        where('criador', '==', uid),
        where('status', 'in', accepted)
      );
      const q2 = fsQuery(
        collection(db, 'socios'),
        where('convidado', '==', uid),
        where('status', 'in', accepted)
      );
      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const set = new Set();
      s1.forEach((d) => d.data()?.convidado && set.add(d.data().convidado));
      s2.forEach((d) => d.data()?.criador && set.add(d.data().criador));
      socios = [...set];
    }
  } catch {
    /* segue sem sócios */
  }

  const parceiros = [];
  return { me: uid, socios, parceiros };
}

// Alias para manter compatibilidade com quem importa getAlgoliaClient
export const getAlgoliaClient = getSearchClient;

/* =========================
   Sugestões de bairros (autocomplete)
   ========================= */
export async function searchBairroSuggestions(prefix, options = {}) {
  const q = (prefix || '').trim();
  if (q.length < 2) return [];

  const engine = await getSearchClient({});
  if (engine.type !== 'algolia') return [];

  // sempre use o índice BASE (não réplicas) para SFFV
  const baseIndex = (engine.indexName || ENV_INDEX).split('_by_')[0]; // remove sufixo de réplica se houver
  const index = engine.client.initIndex(baseIndex);

  const toSlugLocal = (s = '') =>
    String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // ----- filters somente com facets filterable -----
  const parts = [];
  if (options.publicado === true) parts.push('publicado:true');
  if (options.estado) parts.push(`estado_slug:${toSlugLocal(options.estado)}`);
  if (options.cidade) parts.push(`cidade_slug:${toSlugLocal(options.cidade)}`);

  // Meus / Sócios / Parceiros (OR de owners)
  if (Array.isArray(options.ownerIds) && options.ownerIds.length) {
    const or = options.ownerIds
      .filter(Boolean)
      .map((uid) => `ownerId:"${uid}"`);
    if (or.length) parts.push(`(${or.join(' OR ')})`);
  } else if (options.ownerId) {
    parts.push(`ownerId:"${options.ownerId}"`);
  }

  const sffvParams = parts.length
    ? { filters: parts.join(' AND '), maxFacetHits: 10 }
    : { maxFacetHits: 10 };

  // Sem cidade/UF -> facet composto; com cidade/UF -> facet 'bairro'
  const useComposed = !(options.cidade || options.estado);
  const facetName = useComposed ? 'bairro_cidade_uf' : 'bairro';

  // helper de mapeamento
  const mapComposed = (hits) =>
    hits.map((h) => {
      const [bairro = '', cidade = '', estado = ''] = String(h.value).split(
        '|||'
      );
      return {
        label: `${bairro} - ${cidade} / ${estado}`,
        bairro,
        cidade,
        estado,
        count: h.count,
        highlighted: h.highlighted,
      };
    });

  const mapBairro = (hits) =>
    hits.map((h) => ({
      label:
        options.cidade && options.estado
          ? `${h.value} - ${options.cidade} / ${options.estado}`
          : h.value,
      bairro: h.value,
      cidade: options.cidade || null,
      estado: options.estado || null,
      count: h.count,
      highlighted: h.highlighted,
    }));

  try {
    const res = await index.searchForFacetValues(facetName, q, sffvParams);
    const hits = res?.facetHits || [];

    if (hits.length) {
      return useComposed ? mapComposed(hits) : mapBairro(hits);
    }

    // 🔁 fallback quando facet composto não retornar nada
    if (useComposed) {
      const res2 = await index.searchForFacetValues('bairro', q, sffvParams);
      return mapBairro(res2?.facetHits || []);
    }

    return [];
  } catch (err) {
    console.warn('[SFFV]', facetName, 'erro:', err?.message || err);

    // 🔁 fallback quando houver erro com o facet composto
    if (useComposed) {
      try {
        const res2 = await index.searchForFacetValues('bairro', q, sffvParams);
        return mapBairro(res2?.facetHits || []);
      } catch (err2) {
        console.warn('[SFFV] fallback bairro erro:', err2?.message || err2);
      }
    }
    return [];
  }
}
